import reportOptimoveLogin from "@casino/client/optimove/reportLogin";
import toGenericErrors from "@casino/legacy/client/components/Auth/Shared/utils/toGenericErrors";
import useSocialAuthLocale from "@casino/legacy/client/features/SocialAuth/hooks/useSocialAuthLocale";
import useUpdateUserLanguage from "@casino/legacy/client/features/SocialAuth/hooks/useUpdateUserLanguage";
import useRouter from "@casino/legacy/client/hooks/router/useRouter";
import useLanguage from "@casino/legacy/client/hooks/useLanguage";
import usePhoneNumberHint from "@casino/legacy/client/hooks/usePhoneNumberHint";
import useLoginError from "@casino/legacy/client/hooks/user/useLoginError";
import startFraudEngine from "@casino/legacy/client/shared/fraud/fraudEngine";
import asyncTimeout from "@casino/legacy/shared/utils/asyncTimeout";
import authType from "@casino/legacy/shared/utils/auth/authType";
import type { ErrorsFragment } from "@workspace/casino_core/mutations/UpdateUserSettingsMutation";
import { getCookie, setCookie } from "@workspace/cookies/browser";
import { track } from "@workspace/tracking";

type HandleLoginArgs = {
  deviceDataToken: string;
  errors?: Array<ErrorsFragment | undefined>;
  type?: AuthType;
  state?: string;
  token?: string;
  userId?: string;
};

const useHandleLogin = () => {
  const { code: language } = useLanguage();
  const router = useRouter();
  const updateUserLanguage = useUpdateUserLanguage();
  const { setError } = useLoginError();
  const [, , clearSocialAuthLocale] = useSocialAuthLocale();
  const socialAuthType = getCookie("social_auth_type");
  const { setPhoneNumberHint } = usePhoneNumberHint();

  const handleLogin = async ({
    token,
    type,
    state,
    deviceDataToken,
    errors,
    userId,
  }: HandleLoginArgs) => {
    if (!errors) {
      if (token) {
        setCookie("token", token);
      }
      startFraudEngine({ deviceDataToken });
      await updateUserLanguage(language);
      track("Login", { userId });
      if (userId) {
        reportOptimoveLogin(userId);
      }

      clearSocialAuthLocale();
      await asyncTimeout(250);
      const redirectFromState = getRedirectFromState(state) || "/";
      const redirectPath =
        redirectFromState.includes("login") ||
        redirectFromState.includes("register")
          ? "/"
          : redirectFromState;
      router.push(redirectPath, undefined);
      if (type) {
        authType.set(type);
      }
      return { success: true };
    }
    const criticalErrors = getCriticalErrors(errors);

    if (criticalErrors) {
      setError(criticalErrors);
      setPhoneNumberHint(toGenericErrors(errors));

      if (socialAuthType) {
        if (criticalErrors.includes("OTP") || criticalErrors === "INVALID") {
          router.push("/two-factor-authentication", undefined);
          return { loginError: true };
        }
      }
      router.push("/login", undefined);
      return { loginError: true };
    }
    return {};
  };
  return handleLogin;
};

const getRedirectFromState = (state = "{}") =>
  JSON.parse(state).redirect as string;

const getCriticalErrors = (errors?: Array<ErrorsFragment | undefined>) =>
  errors
    ?.filter((el) => el?.message !== "IS_REQUIRED")
    .map((el) => el?.message)
    .join(",") ?? "";

export default useHandleLogin;
